import { useEffect, useMemo, useState } from 'react'
import { useAsyncEffect } from 'ahooks'
import { PageProps } from 'gatsby'
import moment, { Moment } from 'moment'
import { decode as decodeBase64 } from 'base-64'
import {
  LedgerQueryVariables,
  LedgerUpdateInput,
  useLedgerExportMutation,
  useLedgerItemsQuery,
  useLedgerQuery,
  useLedgerTransactionCancelMutation,
  useLedgerUpdateMutation,
} from 'lib/graphql/storeadmin'
import {
  Button,
  Collapse,
  ErrorAlert,
  SEO,
  showMessage,
  TextArea,
  Title,
} from 'components/common'
import {
  AddLedgerModal,
  AddLedgerValues,
  DropDownItem,
  ExportLedgerModal,
  LedgerCancelModal,
  LedgersCard,
  Transaction,
} from 'components/ledgers'
import { date as strDate } from 'lib/str-formatter'
import { ledgerErrorHandler } from 'lib/hooks/ledger'
import {
  ellipsisText,
  ledgerReturnTypeCheck,
  ledgerTypeCheck,
} from 'utils/custom-functions'
import { downloadFile } from 'lib/common'

const showCollapse = (note: string) => {
  const title = ellipsisText(note, 10)
  return (
    <>
      {title?.checkLength ? (
        <Collapse expandIconPosition="right" ghost>
          <Collapse.Panel
            header={title?.text}
            key={note}
            style={{ padding: '0px' }}
          >
            <div style={{ padding: '0px' }}>
              <TextArea
                disabled
                style={{ height: '100px', color: '#000000' }}
                defaultValue={note}
              />
            </div>
          </Collapse.Panel>
        </Collapse>
      ) : (
        <div style={{ paddingLeft: '16px' }}> {note}</div>
      )}
    </>
  )
}

export const Head = () => <SEO title="現金出納" />

const LedgerPage = ({ location }, props: PageProps) => {
  const [initialLoading, setInitialLoading] = useState<boolean>(false)

  const [transactionsOnCancel] = useLedgerTransactionCancelMutation()
  const [updateLedger] = useLedgerUpdateMutation()

  const [queryVariables, setQueryVariables] = useState<LedgerQueryVariables>({
    itemId: location?.state?.id || '',
    month: location?.state?.month || moment().format('YYYY-MM'),
  })
  const {
    data: ledgerItemsData,
    loading: ledgerItemsLoading,
    refetch: ledgerItemsRefetch,
    error: ledgerItemsError,
  } = useLedgerItemsQuery()

  const { data, loading, error, refetch } = useLedgerQuery({
    variables: {
      itemId: queryVariables.itemId,
      month: queryVariables.month,
    },
  })

  const [date, setDate] = useState<moment.Moment>(
    moment(location?.state?.month) || moment()
  )

  // Ledger update error useState
  const [showLedgerUpdateErrorAlert, setShowLedgerUpdateErrorAlert] =
    useState(false)
  const [ledgerUpdateErrorDescriptions, setLedgerUpdateErrorDescriptions] =
    useState<string[]>([])

  // 入出金の追加
  const [isVisible, setIsVisible] = useState<boolean>(false)

  // エクスポート
  const [isModal, setIsModal] = useState<boolean>(false)

  // 赤伝
  const [isCancel, setIsCancel] = useState<boolean>(false)
  const [pageTitleAndStatusValue, setPageTitleAndStatusValue] = useState({
    title: location?.state?.title || '',
    status: location?.state?.status || '',
  })

  const [ledgerExport] = useLedgerExportMutation()

  const [target, setTarget] = useState<{
    id: string
    name: string | number
    date: string
  }>({ id: '', name: '', date: '' })

  const finalErrors = ledgerItemsError || error

  useAsyncEffect(async () => {
    await ledgerItemsRefetch()
  }, [location, ledgerItemsRefetch])

  useEffect(() => {
    if (queryVariables.itemId === '' && !ledgerItemsLoading) {
      const active = ledgerItemsData?.ledgerItems?.edges?.find(
        (item) => item?.node.status === 'ACTIVE'
      )

      if (active) {
        const itemId = decodeBase64(active?.node?.id).split(':')[1]
        setQueryVariables({
          itemId: itemId,
          month: date.format('YYYY-MM'),
        })
        setPageTitleAndStatusValue({
          title: `${active?.node?.title}`,
          status: active?.node?.status,
        })
      } else {
        ledgerItemsData?.ledgerItems?.edges?.forEach((item, index) => {
          if (index === 0) {
            const itemId = decodeBase64(item?.node?.id).split(':')[1]
            setQueryVariables({
              itemId: itemId,
              month: date.format('YYYY-MM'),
            })
            setPageTitleAndStatusValue({
              title: `${item?.node?.title}`,
              status: item?.node?.status,
            })
          }
        })
      }
    } else if (queryVariables.itemId !== '') {
      refetch({ ...queryVariables })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ledgerItemsLoading, ledgerItemsData, queryVariables]) //  deps に refetch が含まれていると、永遠に refetch し続ける

  const finalLoading = ledgerItemsLoading || loading

  const subjectsData: Array<DropDownItem> = useMemo(
    () =>
      ledgerItemsData?.ledgerItems?.edges?.map((item) => {
        const { id, title, status } = item.node
        return {
          key: id,
          id: id,
          title: title,
          status: status,
        }
      }),
    [ledgerItemsData]
  )

  const dataSource: Array<Transaction> = useMemo(() => {
    const ledgerData = []
    !finalLoading &&
      data?.ledger?.transactions?.forEach((transaction, index) => {
        const { id, amount, date, type, total, note, number } = transaction

        ledgerData.push({
          key: `${id}-${index}`,
          id: `${index}${id}`,
          number: `${number}`,
          date: strDate(moment(date)),
          type: ledgerTypeCheck(type),
          amount: amount,
          balance: total,
          note: note ? showCollapse(note) : '',
          slip: (
            <Button
              type="link"
              disabled={pageTitleAndStatusValue?.status === 'ARCHIVED'}
              onClick={() => handleOpenCancel(id, number, date)}
            >
              赤伝
            </Button>
          ),
        })
      })
    return ledgerData
  }, [finalLoading, data, pageTitleAndStatusValue])

  const ellipsisPageTitle = useMemo(() => {
    const title = ellipsisText(pageTitleAndStatusValue.title, 10)
    return title?.text
  }, [pageTitleAndStatusValue])

  const handleExportClose = () => setIsModal(false)
  const handleExport = async (date: Moment) => {
    try {
      setInitialLoading(true)
      const { data: exportData } = await ledgerExport({
        variables: {
          month: moment(date).format('YYYY-MM'),
        },
      })
      const { ledgerUserErrors, url } = exportData.ledgerExport
      if (ledgerUserErrors?.length > 0) {
        const errorDescriptions = ledgerErrorHandler(ledgerUserErrors)
        setLedgerUpdateErrorDescriptions(errorDescriptions)
        setShowLedgerUpdateErrorAlert(true)
        showMessage(
          'error',
          <>
            <div>
              エクスポート時にエラーが発生しました。再度お試しください。
            </div>
            <div>{errorDescriptions?.join('\n')}</div>
          </>
        )
      } else {
        setInitialLoading(false)
        setIsVisible(false)
        downloadFile(url)
      }
    } catch (e) {
      const errorDescriptions =
        e.message.search("The specified 'month' does not exist") > -1 &&
        '入出金データが存在しません'
      console.error(e)
      setInitialLoading(false)
      setIsVisible(false)
      showMessage(
        'error',
        <>
          <div>エクスポート時に予期せぬエラーが発生しました。</div>
          {errorDescriptions && <div>{errorDescriptions}</div>}
        </>
      )
    } finally {
      handleExportClose()
    }
  }

  const handleClose = () => setIsVisible(false)

  const handleFinish = async (values: AddLedgerValues) => {
    try {
      setInitialLoading(true)
      const {
        date: submitDate,
        class: type,
        amount,
        note,
        orderNumber,
      } = values
      const item = subjectsData.find(({ title }) => {
        return title === pageTitleAndStatusValue.title
      })
      const idDecode = decodeBase64(item?.id).split(':')[1]
      const input: LedgerUpdateInput = {
        date: moment(submitDate).format('YYYY-MM-DD'),
        ledgerItemId: idDecode,
        type: ledgerReturnTypeCheck(type),
        amount: amount,
        note: note ? note : null,
        orderNumber: orderNumber,
      }
      const { data: ledgerUpdateData } = await updateLedger({
        variables: {
          month: moment(submitDate).format('YYYY-MM'),
          inputs: input,
        },
      })

      const updateError = ledgerUpdateData?.ledgerUpdate?.ledgerUserErrors
      if (updateError?.length > 0) {
        const errorDescriptions = ledgerErrorHandler(updateError)
        setLedgerUpdateErrorDescriptions(errorDescriptions)
        setShowLedgerUpdateErrorAlert(true)
        showMessage(
          'error',
          <>
            <div>
              現金出納更新時に何らかのエラーが発生しました。再度お試しください。
            </div>
            <div>{errorDescriptions?.join('\n')}</div>
          </>
        )
      } else {
        setIsVisible(false)
        showMessage('success', '現金出納を更新しました。')
        setInitialLoading(false)
        setDate(moment(submitDate))
        setQueryVariables({
          itemId: idDecode,
          month: moment(submitDate).format('YYYY-MM'),
        })
      }
    } catch (e) {
      console.error(e)
      const errorMessages =
        e.message.search(
          'Invalid character: the string to be decoded is not correctly encoded.'
        ) > -1 && '現金出納項目を登録してください。'
      const archivedErrorMessage =
        e.message.search('The specified "ledger_item" is archived') > -1 &&
        '項目はアーカイブされています。'
      showMessage(
        'error',
        <>
          <div>現金出納更新時に予期せぬエラーが発生しました。</div>
          {errorMessages && <div>{errorMessages}</div>}
          {archivedErrorMessage && <div>{archivedErrorMessage}</div>}
        </>
      )
      setLedgerUpdateErrorDescriptions([
        errorMessages || archivedErrorMessage || null,
      ])
      setShowLedgerUpdateErrorAlert(true)
    } finally {
      setInitialLoading(false)
      setIsVisible(false)
    }
  }

  const handleCancelClose = () => setIsCancel(false)

  const handleOpenCancel = (
    id: string,
    name: string | number,
    date: Moment
  ) => {
    setTarget({ id: id, name: name, date: moment(date).format('YYYY-MM') })
    setIsCancel(true)
  }
  const handleCancel = async () => {
    try {
      const { data } = await transactionsOnCancel({
        variables: {
          month: moment(target.date).format('YYYY-MM'),
          transactionId: target.id,
        },
      })
      const { ledgerUserErrors } = data.ledgerTransactionCancel
      if (ledgerUserErrors?.length > 0) {
        const errorDescriptions = ledgerErrorHandler(ledgerUserErrors)
        setLedgerUpdateErrorDescriptions(errorDescriptions)
        setShowLedgerUpdateErrorAlert(true)
        showMessage(
          'error',
          <>
            <div>
              {`No.${target.name} の赤伝処理時にエラーが発生しました。再度お試しください。'`}
            </div>
            <div>{errorDescriptions?.join('\n')}</div>
          </>
        )
      } else {
        showMessage(
          'success',
          <>
            <div>{`No.${target.name} の赤伝を作成しました。`}</div>
          </>
        )
      }
    } catch (e) {
      setShowLedgerUpdateErrorAlert(true)
      console.error(e)
      const errorDescriptions =
        e.message.search('The specified "ledger_item" is archived') > -1 &&
        'アーカイブされたアイテムのトランセクション赤伝を行うことはできません。'
      showMessage(
        'error',
        <>
          <div> '赤伝処理時に予期せぬエラーが発生しました。'</div>
          {errorDescriptions && <div>{errorDescriptions} </div>}
        </>
      )

      setLedgerUpdateErrorDescriptions([errorDescriptions])
    } finally {
      handleCancelClose()
    }
  }

  const onChangeDropDown = (title: string, status: string) => {
    const item = subjectsData.find(({ title: ledgerItemTitle }) => {
      return ledgerItemTitle === title
    })
    const idDecode = decodeBase64(item?.id).split(':')[1]
    setQueryVariables({
      itemId: idDecode,
      month: date.format('YYYY-MM'),
    })
    setPageTitleAndStatusValue({
      title: item.title,
      status: item.status,
    })
  }

  const onChangeDate = (date: moment.Moment) => {
    setDate(date)
    setQueryVariables({
      itemId: queryVariables.itemId,
      month: date.format('YYYY-MM'),
    })
  }
  const onCloseAlert = () => setShowLedgerUpdateErrorAlert(false)
  const isDataEmpty = dataSource?.length <= 0 || subjectsData?.length <= 0

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <Title>{ellipsisPageTitle || '現金出納'}</Title>
        <div className="">
          <Button onClick={() => setIsModal(true)} disabled={isDataEmpty}>
            エクスポートする
          </Button>
          <Button
            className="ml-4"
            type="primary"
            onClick={() => setIsVisible(true)}
            disabled={
              subjectsData?.length <= 0 ||
              pageTitleAndStatusValue?.status === 'ARCHIVED'
            }
          >
            入出金を追加する
          </Button>
        </div>
      </div>
      {showLedgerUpdateErrorAlert && (
        <ErrorAlert
          alertDescription={ledgerUpdateErrorDescriptions}
          onCloseAlert={onCloseAlert}
        />
      )}
      <LedgersCard
        loading={finalLoading || initialLoading}
        dataSource={finalErrors ? [] : dataSource}
        date={date}
        handleViewDay={onChangeDate}
        handleDropDown={onChangeDropDown}
        dropDownDefaultValue={pageTitleAndStatusValue}
        subjects={subjectsData}
        subjectsLoading={ledgerItemsLoading}
        openAddModal={() => setIsVisible(true)}
      />
      <AddLedgerModal
        date={date}
        visible={isVisible}
        onOk={handleFinish}
        onCancel={handleClose}
        loading={initialLoading}
      />
      <ExportLedgerModal
        visible={isModal}
        onOk={handleExport}
        onCancel={() => setIsModal(false)}
        loading={initialLoading}
      />
      <LedgerCancelModal
        name={target.name}
        visible={isCancel}
        onCancel={handleCancelClose}
        onOk={handleCancel}
      />
    </>
  )
}

export default LedgerPage
